import React, { FC } from 'react';

import { FormikErrors, useFormikContext } from 'formik';

import SearchableSelect from '../../../../../../components/inputs/searchableSelect';
import TextInput from '../../../../../../components/inputs/textInput';
import AampeItalicText from '../../../../../../components/shared/AampeText/AampeItalicText';
import styled from 'styled-components';

import aampeTheme from '../../../../../../constants/aampeTheme';
import ICONS from '../../../../../../constants/icons';
import {
    androidAddButtonElement,
    NotificationAndroidActionType,
    NotificationAndroidOptions,
} from './constants';

import { checkButtonTextLength, maxLength } from './helpers';

import {
    NotificationButtonElement,
    NotificationButtonFormValues,
} from '../../../../../../models/formula.model';

interface AndroidButtonFieldsProps {
    index: number;
    errors: FormikErrors<NotificationButtonElement[]>;
    fieldPrefixKey: string;
    value: NotificationButtonElement;
    valLength: number;
    isDisabled: boolean;
    remove: (index: number) => void;
}
const AndroidButtonFields: FC<AndroidButtonFieldsProps> = ({
    index,
    valLength,
    errors,
    fieldPrefixKey,
    value,
    isDisabled,
    remove,
}) => {
    const { setFieldValue, handleBlur, touched } =
        useFormikContext<NotificationButtonFormValues>();

    const handleDelete = () => {
        if (!isDisabled) {
            if (valLength > 1) {
                return remove(index);
            }
            return setFieldValue('androidButtons', [
                {
                    ...androidAddButtonElement,
                },
            ]);
        }
    };
    return (
        <Container>
            <Content>
                <Title isBold>Button {index + 1}</Title>
                <StyledDeleteIcon
                    isDisabled={isDisabled}
                    onClick={handleDelete}
                />
            </Content>
            <FieldContainer>
                <Title>
                    Button text
                    <AampeItalicText text={'(Required)'} />
                </Title>
                <StyledTextField
                    placeholder={`Enter button text (${maxLength(
                        valLength,
                    )} characters)`}
                    value={value?.text!}
                    onChange={(e) =>
                        setFieldValue(`${fieldPrefixKey}.text`, e.target.value)
                    }
                    isDisabled={isDisabled}
                />
                <ErrorMessage>
                    {value?.text
                        ? checkButtonTextLength(value?.text!, valLength) ||
                          (errors?.[index] as any)?.text
                        : ''}
                </ErrorMessage>
            </FieldContainer>
            <SelectFieldContainer>
                <Title>
                    Action type <AampeItalicText text={'(Required)'} />
                </Title>
                <SearchableSelect
                    name={value?.action!}
                    options={NotificationAndroidOptions}
                    onChange={(field, value) => {
                        setFieldValue(
                            `${fieldPrefixKey}.action`,
                            (value as any).value,
                        );
                    }}
                    value={NotificationAndroidOptions.find(
                        (item) => item.value === value?.action!,
                    )}
                    placeholder={'Select action type'}
                    menuPlacement={'top'}
                    isDisabled={isDisabled}
                />
            </SelectFieldContainer>
            {[
                NotificationAndroidActionType.DEEP_LINK,
                NotificationAndroidActionType.URI,
            ].includes(value?.action as NotificationAndroidActionType) && (
                <FieldContainer>
                    <Title>URL</Title>
                    <StyledTextField
                        placeholder="Enter url"
                        value={value?.uri!}
                        onChange={(e) =>
                            setFieldValue(
                                `${fieldPrefixKey}.uri`,
                                e.target.value,
                            )
                        }
                        name={'uri'}
                        onBlur={handleBlur}
                    />
                    <ErrorMessage>
                        {!!value?.uri &&
                            (touched as any)?.uri &&
                            (errors?.[index] as any)?.uri}
                    </ErrorMessage>
                </FieldContainer>
            )}
        </Container>
    );
};

const Container = styled.div({
    backgroundColor: aampeTheme.colors.backgroundWG02,
    borderRadius: aampeTheme.borderRadius.default,
    padding: aampeTheme.spacing4(4),
    gap: aampeTheme.spacing4(4),
    display: 'flex',
    flexDirection: 'column',
});

const StyledDeleteIcon = styled(ICONS.trash)<{ isDisabled: boolean }>(
    ({ isDisabled }) => ({
        color: aampeTheme.colors.textAccessibleWG01,
        width: '20px',
        height: '20px',
        cursor: isDisabled ? 'default' : 'pointer',
    }),
);

const Content = styled.div({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
});

const FieldContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing4(2),
});

const SelectFieldContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing4(2),
    zIndex: 15,
});
const StyledTextField = styled(TextInput)({
    width: '100%',
    '& .MuiInputBase-root': {
        backgroundColor: aampeTheme.colors.white,
        borderRadius: aampeTheme.borderRadius.default,
        borderColor: aampeTheme.colors.bordersWG01,
        minHeight: '38px',
        '&:hover fieldset': {
            borderColor: aampeTheme.colors.solidA01,
        },
    },
});

const ErrorMessage = styled.span({
    color: aampeTheme.colors.error,
    fontSize: aampeTheme.size.font.textSmall,
});

const Title = styled.div<{ isBold?: boolean }>(({ isBold }) => ({
    fontSize: aampeTheme.size.font.default,
    fontWeight: isBold ? aampeTheme.weights.medium : aampeTheme.weights.light,
    lineHeight: aampeTheme.size.lineHeight.small,
    color: aampeTheme.colors.neutral09,
}));
export default AndroidButtonFields;
